import { inject, injectable } from 'inversify';
import { Observable, firstValueFrom, from, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retry, take, tap } from 'rxjs/operators';

import { FileUpload } from '../models/file-upload';
import { Logger } from '../models/logger';
import { GAContants } from '../types/ga.types';
import { OriginalInputFile, ProcessorMetadata } from '../types/types';
import { castArray, isString } from '../utils/lodash-min';
import { ProcessorMetadataUtils } from '../utils/processor-metadata.utils';
import { GAService } from './analytics.service';
import { ApiConfig } from './api-config';
import { EnforcementErrorService } from './enforcement-error.service';
import { FileManagerService } from './file-manager.service';
import { HelperService } from './helper.service';
import { LightHttp } from './light-http.service';
import { OfflineTaskService } from './offline-task.service';
import { SettingsService } from './settings.service';
import { WebSocketService } from './websocket.service';


export interface ProbedData {
    approxTime: number;
    approxSize: number;
}

/**
 * Full POST /files/source/probe response surface: mp4/webm branch fields plus
 * the gif estimator branch (ironman GifProbeEstimator; -1 marks unavailable).
 */
export interface SourceProbeInfo extends Partial<ProbedData> {
    /** milliseconds, matching the mp4 branch */
    originalDuration?: number;
    originalSize?: number;
    naturalGifEstimate?: number;
    floorSize?: number;
    quickOnly?: boolean;
    error?: boolean;
}

export interface SourceProbeParams {
    /** Edge that holds the uploaded file (uploadDomain of its metadata). */
    processorMetadata: ProcessorMetadata;
    /** Upload responses identifying the already-stored files on that edge. */
    originalInputFiles: OriginalInputFile[];
    id: string;
    fromType: string | string[];
    toType: string;
    processType?: string;
    options: any;
    /** Sample seconds for the mp4/webm branch (the gif branch fixes ~1s). */
    probeDuration?: number;
    /** true = quick section only (no sample encodes) — the gif branch's Stage 1. */
    estimateOnly?: boolean;
}

export interface FileProcessResponse {
    originalInputFiles: string[];
}

@injectable()
export class FileProcessService {
    private readonly logger = Logger.getLogger('FileProcessService');

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(FileManagerService) private fileManagerService: FileManagerService,
        @inject(GAService) private gaService: GAService,
        @inject(OfflineTaskService) private offlineTaskService: OfflineTaskService,
        @inject(HelperService) private helperService: HelperService,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(SettingsService) private settingsService: SettingsService,
        @inject(EnforcementErrorService) private enforcementErrorService: EnforcementErrorService,
    ) { }

    /**
     * RxJS retry helper that bails immediately on enforcement / billing
     * rejections (HTTP 429 enforcement_error, HTTP 402
     * insufficient_credits) and surfaces them via the global
     * EnforcementErrorModal. Without this, the convert / schedule path
     * would silently retry 429s with no UI surface (runbook bug 6a-B1).
     */
    private retryConfigWithEnforcementBailout(count: number, delayMs: number) {
        return {
            count,
            delay: (error: unknown) => {
                if (EnforcementErrorService.isEnforcementError(error)) {
                    this.enforcementErrorService.show(
                        EnforcementErrorService.extractDetails(error),
                    );
                    return throwError(() => error);
                }
                return timer(delayMs);
            },
        };
    }

    processFile(fileUploads: FileUpload): Observable<FileProcessResponse[] | any> {
        return from(this.wsService.ensureWsDomain()).pipe(
            mergeMap(() => this._processFileInternal(fileUploads)),
        );
    }

    private _processFileInternal(fileUploads: FileUpload): Observable<FileProcessResponse[] | any> {
        this.gaService.send(
            GAContants.HitType.EVENT,
            GAContants.EventCategory.BUTTON,
            GAContants.EventAction['FILE-CONVERT-START'],
            GAContants.EventLabel.CONVERT,
            '',
        );
        this.fileManagerService.saveFileUploadToStorage(fileUploads);
        this.logger.trace7({ message: 'FileUpload sent for processing', data: FileUpload.toLoggableObject(fileUploads) });
        const processorMetadata = fileUploads.endpointData.processorMetadataList[0];

        return from(firstValueFrom(this.settingsService.featureFlags.pipe(take(1)))).pipe(
            mergeMap((ff) => {
                const hasUnuploadedFiles = fileUploads.file && !FileUpload.isUploadedFile(fileUploads);
                if (ff?.useTaskScheduling && !hasUnuploadedFiles) {
                    return this._scheduleTask(fileUploads, processorMetadata);
                }
                const updateProcessDomain$ = fileUploads.updateProcessDomain(
                    this.http, this.fileManagerService,
                    this.apiConfig.getUploadAndProcessEndpointResolveEndpoint(), this.wsService,
                );
                return updateProcessDomain$.pipe(
                    mergeMap(() => this._processFile(fileUploads, processorMetadata)),
                );
            }),
        );
    }

    private _scheduleTask(fileUploads: FileUpload, processorMetadata: ProcessorMetadata) {
        const url = this.apiConfig.getScheduleTaskEndpoint();
        const formData = this._buildFormData(fileUploads);
        return this.http.request<any>(url, 'POST', formData,
            { headers: this.helperService.getCommonHttpHeaders(), timeout: 30000 })
            .response$.pipe(
                retry(this.retryConfigWithEnforcementBailout(2, 1000)),
                tap((response) => {
                    if (response.processDomain) processorMetadata.processDomain = response.processDomain;
                    if (response.downloadDomain) processorMetadata.downloadDomain = response.downloadDomain;
                    fileUploads.persistFileUpload(this.fileManagerService);
                    this.logger.trace7({ message: 'Task scheduled', data: { workflowId: response.workflowId } });
                    this.gaService.send(GAContants.HitType.EVENT, GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-END'], GAContants.EventLabel.CONVERT, '');
                }),
                catchError((err) => {
                    this.logger.error({ message: `Error scheduling task: ${err.message}`, error: err });
                    this.gaService.send(GAContants.HitType.EVENT, GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-FAILED'], GAContants.EventLabel.CONVERT, '');
                    // Tag enforcement errors so the component can show retry UI instead of removing the file
                    if (err.response?.dataType === 'enforcement_error') {
                        err.isEnforcementError = true;
                        err.enforcementData = err.response;
                    }
                    return throwError(() => err);
                }),
            );
    }

    private _buildFormData(fileUploads: FileUpload): FormData {
        const formData = new FormData();
        if (fileUploads.originalInputFiles) {
            formData.append('originalInputFiles', JSON.stringify(fileUploads.originalInputFiles));
        }
        formData.append('id', fileUploads.id);
        formData.append('processType', fileUploads.processType);
        formData.append('fromType', fileUploads.fromType.toString());
        formData.append('toType', fileUploads.toType);
        formData.append('wsId', this.wsService.getId());
        formData.append('wsDomain', this.wsService.getDomain());
        formData.append('email', fileUploads.settings.email);
        formData.append('featurePage', fileUploads.pageData?.path || '');
        fileUploads.metadataList.map(m => m.processorMetadataList[0]).forEach(pmdl => {
            formData.append('fileUploadedServerEndpointList',
                ProcessorMetadataUtils.getUploadedFileDownloadEndpoint(pmdl));
        });
        formData.append('metadataList', JSON.stringify(fileUploads.metadataList));
        formData.append('options', JSON.stringify(fileUploads.options));
        formData.append('optionsSnapshot', JSON.stringify(fileUploads.optionsSnapshot));
        return formData;
    }


    private _processFile(fileUploads: FileUpload, processorMetadata: ProcessorMetadata,) {
        // console.log('HPY =========== processFile mergeMap');
        const url = ProcessorMetadataUtils.getProcessorEndpoint(processorMetadata);
        const formData: FormData = new FormData();
        if (fileUploads.file && !FileUpload.isUploadedFile(fileUploads)) {
            fileUploads.file.forEach(file => {
                formData.append('files', file, file.name); // [' + (i++) + ']
            });
        } else {
            // this is a re add event
            if (fileUploads.originalInputFiles) {
                formData.append('originalInputFiles', JSON.stringify(fileUploads.originalInputFiles));
            }
        }

        formData.append('id', fileUploads.id);
        formData.append('processType', fileUploads.processType);
        formData.append('fromType', fileUploads.fromType.toString());
        formData.append('toType', fileUploads.toType);
        formData.append('wsId', this.wsService.getId());
        formData.append('wsDomain', this.wsService.getDomain());
        formData.append('email', fileUploads.settings.email);
        formData.append('featurePage', fileUploads.pageData?.path || '');
        fileUploads.metadataList.map(m => m.processorMetadataList[0]).forEach(pmdl => {
            formData.append(
                'fileUploadedServerEndpointList',
                ProcessorMetadataUtils.getUploadedFileDownloadEndpoint(pmdl)
            );
        });

        formData.append(
            'metadataList',
            JSON.stringify(fileUploads.metadataList),
        );
        // HPY IMP this is needed to dispatch the email. it is used in the email
        // formData.append('downloadURLPrefix', fileUploads.endpointData.domain + fileUploads.endpointData.download);
        /*
        const options = {};
        fileUploads.options.forEach(option => {
            const optionKeys = Object.keys(option);
            optionKeys.forEach(key => {
                options[key] = option[key];
            });
        });
        */
        formData.append('options', JSON.stringify(fileUploads.options));
        formData.append('optionsSnapshot', JSON.stringify(fileUploads.optionsSnapshot));

        return this.http.request<FileProcessResponse>(
            url,
            'POST',
            formData,
            { headers: this.helperService.getCommonHttpHeaders(), timeout: 30000 })
            .response$
            .pipe(
                retry(this.retryConfigWithEnforcementBailout(2, 1000)),
                tap(() => {
                    this.logger.trace7({ message: 'File process success: ', data: FileUpload.toLoggableObject(fileUploads) });
                    this.gaService.send(
                        GAContants.HitType.EVENT,
                        GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-END'],
                        GAContants.EventLabel.CONVERT,
                        '',
                    );
                }),
                catchError((err) => {
                    // console.log('HPY =========== processFile catchError');
                    this.logger.error({ message: `Error processing file: ${err.message}`, error: err });
                    this.gaService.send(
                        GAContants.HitType.EVENT,
                        GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-FAILED'],
                        GAContants.EventLabel.CONVERT,
                        '',
                    );
                    return throwError(() => err);
                }),
            );
    }
    /**
     * Blocking call
     */
    async createZip(fileUpload: FileUpload): Promise<any> {
        this.logger.trace7({ message: `Creating zip file.`, data: FileUpload.toLoggableObject(fileUpload) });
        const processorMetadata = fileUpload.endpointData.processorMetadataList[0];
        return new Promise(async (resolve, reject) => {
            if (!processorMetadata.processDomain) {
                reject(new Error('File is not processed!')); return;
            }
            const url = ProcessorMetadataUtils.getZipEndpoint(processorMetadata);
            const formData: FormData = new FormData();

            let result = fileUpload.result;
            if (isString(result)) {
                try { result = JSON.parse(fileUpload.result); } catch (e) { reject(e); return; }
            }




            const zipFile = result.zip;
            const outputFiles = castArray(result.output);

            formData.append('id', fileUpload.id);
            formData.append('wsId', this.wsService.getId());
            formData.append('wsDomain', this.wsService.getDomain());
            formData.append('zipFile', zipFile);
            (outputFiles || []).forEach(file => {
                formData.append('outputFiles', file);
            });

            await firstValueFrom(this.http.request(url, 'POST', formData, { headers: this.helperService.getCommonHttpHeaders() })
                .response$)
                .then(resp => {
                    this.logger.trace7({ message: `Creating zip file success.`, data: resp });
                    resolve(fileUpload);
                })
                .catch(err => {
                    this.logger.error({ message: `Error creating zip file.`, data: FileUpload.toLoggableObject(fileUpload), error: err });
                    reject(err);
                });
        });
    }

    /**
     * Sample-encode estimator probe — the ONE correct implementation.
     *
     * Replaces the previous probeProcessForInfo, which was dead code twice
     * over: it posted to /files/input/probe (a URL that never existed on
     * ironman — the real mapping is /files/source/probe) and appended raw
     * multipart file bytes, which the endpoint doesn't accept (it resolves
     * `originalInputFiles` references to files already uploaded to that
     * edge). Call POST-UPLOAD only, against the edge holding the file.
     */
    async probeSourceForInfo(params: SourceProbeParams): Promise<SourceProbeInfo> {
        const url = ApiConfig.suffixDomainWithHttpsEdgeEndpoint(params.processorMetadata.uploadDomain) + '/files/source/probe';
        const formData: FormData = new FormData();
        formData.append('originalInputFiles', JSON.stringify(params.originalInputFiles));
        formData.append('probeDuration', String(params.probeDuration ?? 1));
        formData.append('id', params.id);
        if (params.processType) {
            formData.append('processType', params.processType);
        }
        formData.append('fromType', castArray(params.fromType).toString());
        formData.append('toType', params.toType);
        formData.append('wsId', this.wsService.getId());
        formData.append('wsDomain', this.wsService.getDomain());
        formData.append('options', JSON.stringify(params.options ?? {}));
        formData.append('log', 'false');
        formData.append('estimateOnly', String(!!params.estimateOnly));

        this.logger.trace7({ message: 'Probing source for info.', data: { id: params.id, toType: params.toType, estimateOnly: !!params.estimateOnly } });
        try {
            const data = await firstValueFrom(this.http.request<SourceProbeInfo>(
                url, 'POST', formData,
                { headers: this.helperService.getCommonHttpHeaders(), timeout: 180000 },
            ).response$);
            this.logger.trace7({ message: 'Success probing source for info.', data });
            return (data as any)?.body ?? data;
        } catch (err) {
            this.logger.error({ message: 'Error probing source for info.', data: { id: params.id }, error: err });
            throw err;
        }
    }
}
